import React, { JSX, useEffect, useState } from "react";
import { Table, DatePicker, Spin, Button, Space, message } from "antd";
import { FileExcelOutlined } from "@ant-design/icons";
import { useAuth } from "../../store/AuthContext";
import { PERMS } from "../../constants/permissions";
import { exportToExcel, exportStamp, ExcelSheet } from "../../utils/excelExport";
import { useReportApi } from "../../store/reportContext";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import {
    BarChartOutlined,
    DollarCircleOutlined,
    ShoppingCartOutlined,
    UndoOutlined,
    CreditCardOutlined,
    TeamOutlined,
    FileDoneOutlined
} from "@ant-design/icons";

const { RangePicker } = DatePicker;

const keyIcons: Record<string, JSX.Element> = {
    sales: <ShoppingCartOutlined style={{ color: "#38bdf8" }} />,
    purchases: <DollarCircleOutlined style={{ color: "#4ade80" }} />,
    returns: <UndoOutlined style={{ color: "#fb7185" }} />,
    expenses: <CreditCardOutlined style={{ color: "#fbbf24" }} />,
    summary: <BarChartOutlined style={{ color: "#6366f1" }} />,
    customers: <TeamOutlined style={{ color: "#a21caf" }} />,
    transactions: <FileDoneOutlined style={{ color: "#0284c7" }} />,
};

const columnsMap: Record<string, any[]> = {
    sales: [
        { title: "currency", dataIndex: "_id" },
        { title: "total_sales", dataIndex: "totalSales", align: "right" },
        { title: "total_paid", dataIndex: "totalPaid", align: "right" },
        { title: "total_remain", dataIndex: "totalRemain", align: "right" },
        { title: "total_unpaid", dataIndex: "totalUnpaid", align: "right" },
        { title: "transactions", dataIndex: "transactions", align: "right" }
    ],
    purchases: [
        { title: "currency", dataIndex: "_id" },
        { title: "total_purchases", dataIndex: "totalPurchases", align: "right" },
        { title: "total_paid", dataIndex: "totalPaid", align: "right" },
        { title: "total_remain", dataIndex: "totalRemain", align: "right" },
        { title: "transactions", dataIndex: "transactions", align: "right" }
    ],
    returns: [
        { title: "currency", dataIndex: "_id" },
        { title: "total_returns", dataIndex: "totalReturns", align: "right" },
        { title: "total_paid", dataIndex: "totalPaid", align: "right" },
        { title: "total_remain", dataIndex: "totalRemain", align: "right" },
        { title: "transactions", dataIndex: "transactions", align: "right" }
    ],
    expenses: [
        { title: "currency", dataIndex: "_id" },
        { title: "total_expenses", dataIndex: "totalExpenses", align: "right" },
        { title: "transactions", dataIndex: "transactions", align: "right" }
    ],
    repayments: [
        { title: "currency", dataIndex: "_id" },
        { title: "totalLoanRepayments", dataIndex: "totalLoanRepayments", align: "right" },
        { title: "transactions", dataIndex: "transactions", align: "right" }
    ],
    supplierPayments: [
        { title: "currency", dataIndex: "_id" },
        { title: "totalSupplierPayments", dataIndex: "totalSupplierPayments", align: "right" },
        { title: "transactions", dataIndex: "transactions", align: "right" }
    ]
};

// Dynamically build columns from actual API data keys, falling back to columnsMap
function buildColumns(key: string, rows: any[], t: (k: string) => string): any[] {
    if (!rows || rows.length === 0) return (columnsMap[key] || []).map(col => ({ ...col, title: t(col.title) }));
    const knownKeys = new Set((columnsMap[key] || []).map((c: any) => c.dataIndex));
    const dynamicKeys = Object.keys(rows[0]).filter(k => !knownKeys.has(k) && k !== '_id' && k !== '__v');
    const base = (columnsMap[key] || Object.keys(rows[0]).map(k => ({ title: k, dataIndex: k, align: 'right' as const })))
        .map(col => ({
            ...col,
            title: t(col.title),
            render: (value: any) => typeof value === 'number' ? value.toLocaleString() : (value ?? '-'),
        }));
    const extra = dynamicKeys.map(k => ({
        title: t(k),
        dataIndex: k,
        align: 'right' as const,
        render: (value: any) => typeof value === 'number' ? value.toLocaleString() : (value ?? '-'),
    }));
    return [...base, ...extra];
}

/**
 * Flatten one section of the summary into rows an Excel sheet can hold.
 * Nested objects are stringified rather than dropped, so nothing is silently
 * lost from the export.
 */
function toSheetRows(rows: any[]): Record<string, any>[] {
    return rows.map(row =>
        Object.fromEntries(
            Object.entries(row)
                .filter(([key]) => key !== "__v")
                .map(([key, value]) => [
                    key,
                    value && typeof value === "object" ? JSON.stringify(value) : value,
                ])
        )
    );
}

const DailySummaryTable: React.FC = () => {
    const { fetchDailySummary } = useReportApi();
    const { t } = useTranslation();
    const { hasAnyPermission } = useAuth();
    const [data, setData] = useState<any>({});
    const [loading, setLoading] = useState(false);
    const [dateRange, setDateRange] = useState<[any, any]>([dayjs().startOf("month"), dayjs()]);

    const canExport = hasAnyPermission(
        PERMS.exportData.excel,
        PERMS.exportData.report,
        PERMS.report.export
    );

    // Every section of the summary becomes its own sheet in one workbook.
    const handleExport = () => {
        const sheets: ExcelSheet[] = Object.entries(data)
            .filter(([, value]) => Array.isArray(value) && (value as any[]).length > 0)
            .map(([key, value]) => {
                const rows = toSheetRows(value as any[]);
                return {
                    name: t(key) || key,
                    columns: Object.keys(rows[0]).map(column => ({
                        header: t(column) || column,
                        key: column,
                    })),
                    rows,
                };
            });

        if (sheets.length === 0) {
            message.info(t("no_data_found") || "No data to export");
            return;
        }

        exportToExcel({ fileName: `daily-summary-${exportStamp()}`, sheets });
    };

    useEffect(() => {
        setLoading(true);
        fetchDailySummary({
            startDate: dateRange[0].toISOString(),
            endDate: dateRange[1].toISOString(),
        }).then((res: any) => {
            setData(res);
            setLoading(false);
        });
    }, [dateRange, fetchDailySummary]);

    return (
        <div style={{ maxWidth: 1250, margin: "0 auto", padding: "0 6px" }}>
            <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                margin: "18px 0 28px 0"
            }}>
                <div style={{
                    fontSize: 23,
                    fontWeight: 800,
                    letterSpacing: 0.5,
                    color: "#334155",
                    display: "flex",
                    alignItems: "center",
                    gap: 9
                }}>
                    <BarChartOutlined style={{ color: "#22d3ee", fontSize: 28 }} />
                    {t("daily_summary")}
                </div>
                <Space wrap>
                    <RangePicker
                        style={{
                            borderRadius: 8,
                            minWidth: 260,
                            fontWeight: 700,
                            fontSize: 15,
                            boxShadow: "0 1px 6px #e0e7ef"
                        }}
                        value={dateRange}
                        onChange={dates => {
                            if (dates && dates[0] && dates[1]) setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs]);
                        }}
                    />
                    {canExport && (
                        <Button
                            icon={<FileExcelOutlined />}
                            onClick={handleExport}
                            disabled={loading || Object.keys(data).length === 0}
                            style={{
                                background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
                                borderColor: "#15803d",
                                color: "#fff",
                                fontWeight: 600,
                            }}
                        >
                            {t("download_excel") || "Download Excel"}
                        </Button>
                    )}
                </Space>
            </div>
            <Spin spinning={loading} size="large">
                {Object.keys(data).length === 0 && !loading ? (
                    <div style={{
                        border: "1.5px dashed #e5e7eb",
                        borderRadius: 14,
                        padding: 36,
                        background: "#f9fafb",
                        textAlign: "center",
                        margin: "40px auto"
                    }}>
                        <span style={{ fontSize: 18, color: "#64748b" }}>{t("no_data_found")}</span>
                    </div>
                ) : Object.keys(data).map((key) => (
                    columnsMap[key] && (
                        <div
                            key={key}
                            style={{
                                marginBottom: 32,
                                borderRadius: 20,
                                background: "#fff",
                                boxShadow: "0 2px 20px 0 rgba(39,21,107,0.07)",
                                border: "1.5px solid #f1f5f9",
                                overflow: "hidden"
                            }}
                        >
                            <div style={{
                                background: "#f3f4f6",
                                padding: "14px 24px",
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                borderBottom: "1.5px solid #e5e7eb"
                            }}>
                                {keyIcons[key] || <BarChartOutlined style={{ color: "#6366f1" }} />}
                                <span style={{
                                    fontWeight: 700,
                                    fontSize: 18,
                                    letterSpacing: 0.3,
                                    color: "#334155"
                                }}>{t(key)}</span>
                            </div>
                            <div style={{ overflowX: "auto", padding: "10px 14px 12px 14px" }}>
                                <Table
                                    columns={buildColumns(key, data[key], t)}
                                    rowKey="_id"
                                    dataSource={data[key]}
                                    pagination={false}
                                    bordered={false}
                                    size="middle"
                                    style={{
                                        minWidth: 800,
                                        background: "transparent"
                                    }}
                                    rowClassName={(_, i) =>
                                        i % 2 === 0 ? "table-row-even" : "table-row-odd"
                                    }
                                />
                                <style>
                                    {`
                  .ant-table {
                    border-radius: 14px !important;
                    overflow: hidden !important;
                  }
                  .ant-table-thead > tr > th {
                    background: #f1f5f9 !important;
                    font-weight: 700 !important;
                    font-size: 15px !important;
                    color: #334155 !important;
                  }
                  .table-row-even {
                    background: #f8fafc !important;
                  }
                  .table-row-odd {
                    background: #f3f4f6 !important;
                  }
                  .ant-table-cell {
                    font-size: 15px;
                    font-weight: 600;
                  }
                `}
                                </style>
                            </div>
                        </div>
                    )
                ))}
            </Spin>
        </div>
    );
};

export default DailySummaryTable;
